import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Tag, Space, message, Empty, Tooltip, Popconfirm } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PercentageOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { financeSettingsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../SkeletonLoaders';

const TaxSettingsTab = forwardRef(({ data, onUpdate }, ref) => {
  const [form] = Form.useForm();
  const [taxes, setTaxes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setTaxes(data.deals || data || []);
    }
  }, [data]);

  const handleAdd = () => {
    setEditingTax(null);
    form.resetFields();
    setModalVisible(true);
  };

  useImperativeHandle(ref, () => ({
    openAddModal: handleAdd,
  }));

  const handleEdit = async (record) => {
    setDetailLoading(true);
    setEditingTax(record);
    setModalVisible(true);
    try {
      const res = await financeSettingsApi.getTax(record.id);
      const tax = res?.tax || res?.data?.tax || record;
      form.setFieldsValue({
        tax_name: tax.tax_name,
        rate_percent: Number(tax.rate_percent),
      });
    } catch (error) {
      console.error('Error fetching tax details:', error);
      form.setFieldsValue({
        tax_name: record.tax_name,
        rate_percent: Number(record.rate_percent),
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await financeSettingsApi.deleteTax(id);
      message.success('مالیات با موفقیت حذف شد');
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting tax:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در حذف مالیات');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        tax_name: values.tax_name,
        rate_percent: values.rate_percent,
      };

      if (editingTax) {
        payload.via = 'tax-setting';
        await financeSettingsApi.updateTax(editingTax.id, payload);
        message.success('مالیات با موفقیت ویرایش شد');
      } else {
        await financeSettingsApi.createTax(payload);
        message.success('مالیات جدید با موفقیت ایجاد شد');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingTax(null);
      onUpdate?.();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error saving tax:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در ذخیره مالیات');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'نام مالیات',
      dataIndex: 'tax_name',
      key: 'tax_name',
      render: (text) => (
        <div className="flex items-center gap-2">
          <PercentageOutlined className="text-green-500" />
          <span className="font-medium">{text}</span>
        </div>
      ),
    },
    {
      title: 'درصد',
      dataIndex: 'rate_percent',
      key: 'rate_percent',
      width: 140,
      align: 'center',
      render: (val) => (
        <Tag icon={<CheckCircleOutlined />} color="blue" style={{ fontSize: 13 }}>
          {val}٪
        </Tag>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              className="text-blue-500 hover:text-blue-600"
            />
          </Tooltip>
          <Popconfirm
            title="حذف مالیات"
            description="آیا از حذف این مالیات اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله، حذف کن"
            cancelText="خیر"
            okButtonProps={{ danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } }}
            cancelButtonProps={{ style: { borderColor: '#d9d9d9', color: '#595959' } }}
          >
            <Tooltip title="حذف">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                className="text-red-500 hover:text-red-600"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <Table
        columns={columns}
        dataSource={taxes}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              description={<span style={{ fontFamily: 'CustomFont' }}>هیچ مالیاتی ثبت نشده</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        style={{ fontFamily: 'CustomFont' }}
      />

      <Modal
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingTax(null);
          form.resetFields();
        }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <PercentageOutlined className="text-green-500" />
            <span>{editingTax ? 'ویرایش مالیات' : 'افزودن مالیات جدید'}</span>
          </div>
        }
        footer={null}
        centered
        width={450}
        destroyOnClose
      >
        {detailLoading ? (
          <TableSkeleton rows={2} columns={1} />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ fontFamily: 'CustomFont' }}
          >
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>نام مالیات</span>}
              name="tax_name"
              rules={[{ required: true, message: 'نام مالیات الزامی است' }]}
            >
              <Input
                placeholder="مثلا: ارزش افزوده"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>درصد مالیات</span>}
              name="rate_percent"
              rules={[{ required: true, message: 'درصد مالیات الزامی است' }]}
            >
              <InputNumber
                min={0}
                max={100}
                placeholder="مثلا: 10"
                addonAfter="٪"
                style={{ width: '100%', fontFamily: 'CustomFont' }}
              />
            </Form.Item>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingTax(null);
                  form.resetFields();
                }}
                style={{ fontFamily: 'CustomFont', borderColor: '#d9d9d9', color: '#595959' }}
              >
                انصراف
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="bg-green-600 hover:bg-green-700 border-0"
                style={{ fontFamily: 'CustomFont' }}
              >
                {editingTax ? 'ذخیره تغییرات' : 'ایجاد'}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
});

export default TaxSettingsTab;
